import * as React from 'react'
import { ScrollArea as ScrollAreaPrimitive } from '@base-ui/react/scroll-area'
import { cn } from '@/shared/lib/utils'

type ScrollAreaVisibility = 'hover' | 'always' | 'scroll' | 'auto'

interface ScrollAreaProps extends ScrollAreaPrimitive.Root.Props {
  orientation?: 'vertical' | 'horizontal'
  /** Optional ref forwarded to the inner scrollable viewport element. */
  viewportRef?: React.Ref<HTMLDivElement>
  /** Radix-compat scrollbar visibility (not passed to Base UI root). @default 'hover' */
  type?: ScrollAreaVisibility
}

function ScrollArea({
  className,
  children,
  orientation = 'vertical',
  viewportRef,
  type = 'hover',
  ...props
}: ScrollAreaProps) {
  return (
    <ScrollAreaPrimitive.Root
      data-slot='scroll-area'
      className={cn('relative', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        data-slot='scroll-area-viewport'
        className={cn(
          'size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1',
          orientation === 'horizontal' && 'overflow-x-auto!'
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar orientation={orientation} type={type} />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
}

function ScrollBar({
  className,
  orientation = 'vertical',
  type = 'hover',
  ...props
}: ScrollAreaPrimitive.Scrollbar.Props & {
  type?: ScrollAreaVisibility
}) {
  return (
    <ScrollAreaPrimitive.Scrollbar
      data-slot='scroll-area-scrollbar'
      orientation={orientation}
      className={cn(
        'flex touch-none p-px select-none transition-colors',
        'data-[orientation=vertical]:h-full data-[orientation=vertical]:w-2.5 data-[orientation=vertical]:border-l data-[orientation=vertical]:border-l-transparent',
        'data-[orientation=horizontal]:h-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:border-t data-[orientation=horizontal]:border-t-transparent',
        type === 'hover' &&
          'pointer-events-none opacity-0 transition-opacity data-hovering:pointer-events-auto data-hovering:opacity-100 data-scrolling:pointer-events-auto data-scrolling:opacity-100 data-scrolling:duration-0',
        type === 'scroll' &&
          'pointer-events-none opacity-0 transition-opacity data-scrolling:pointer-events-auto data-scrolling:opacity-100 data-scrolling:duration-0',
        type === 'auto' &&
          'pointer-events-none opacity-0 transition-opacity data-hovering:pointer-events-auto data-hovering:opacity-100 data-scrolling:pointer-events-auto data-scrolling:opacity-100 data-scrolling:duration-0',
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.Thumb
        data-slot='scroll-area-thumb'
        className='relative flex-1 rounded-full bg-border'
      />
    </ScrollAreaPrimitive.Scrollbar>
  )
}

export { ScrollArea, ScrollBar }
